import React from 'react';
import { Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ImageUploaderProps {
  previewUrl: string | null;
  isEditMode: boolean;
  currentGameCoverUrl: string | null;
  disabled: boolean;
  onFileChange: (file: File | null) => void;
  onPreviewChange: (url: string | null) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ 
  previewUrl, 
  isEditMode, 
  currentGameCoverUrl,
  disabled,
  onFileChange,
  onPreviewChange
}) => {
  // Configurações de segurança para upload
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  
  const validateFile = (file: File): string | null => {
    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      return `Arquivo muito grande. Máximo permitido: ${MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }
    
    // Validar tipo MIME
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Tipo de arquivo não permitido. Use JPG, PNG, GIF ou WebP.';
    }
    
    // Validar extensão do arquivo
    const fileName = file.name.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    
    if (!hasValidExtension) {
      return 'Extensão de arquivo não permitida.';
    }
    
    return null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validar arquivo
      const validationError = validateFile(file);
      if (validationError) {
        alert(validationError);
        e.target.value = ''; // Limpar input
        return;
      }
      
      console.log(`File selected: ${file.name}, size: ${file.size} bytes, type: ${file.type}`);
      onFileChange(file);
    }
  };

  const handleRemoveImage = () => {
    console.log("Removing image");
    // Clear file input value
    const fileInput = document.getElementById('file-upload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
    // Set both file and preview to null
    onFileChange(null);
    // Set preview to null (not the existing URL) to actually remove the image
    onPreviewChange(null);
    // In edit mode, we want to clear the existing image completely
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="cover" className="text-white">Imagem de Capa</Label>
      
      <div className="flex flex-col items-center p-6 border-2 border-dashed border-[#2A3749] rounded-lg bg-[#1A2030] hover:border-[#00B6F0] transition-all">
        {previewUrl ? (
          <div className="flex flex-col items-center">
            <div className="w-40 h-40 mb-4 rounded-lg overflow-hidden border border-[#2A3749]">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="w-full h-full object-cover" 
                onError={(e) => {
                  console.error(`Error loading image preview: ${previewUrl}`);
                  (e.target as HTMLImageElement).src = "https://placehold.co/400x400/252B36/AAAAAA?text=Sem+imagem";
                }}
                onLoad={() => console.log(`Preview image loaded successfully: ${previewUrl}`)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleRemoveImage}
              className="text-gray-400 hover:text-white border-[#2A3749]"
              disabled={disabled}
            >
              Remover imagem
            </Button>
          </div>
        ) : (
          <label 
            htmlFor="file-upload" 
            className="flex flex-col items-center cursor-pointer"
          >
            <Image className="h-10 w-10 text-gray-500 mb-4" />
            <span className="text-[#00B6F0] font-medium mb-2">Clique para fazer upload</span>
            <span className="text-gray-400 text-sm text-center">
              Suporta JPG, PNG, GIF ou WebP até 5MB
            </span>
          </label>
        )}
        <Input
          id="file-upload"
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          onChange={handleFileChange}
          className="hidden"
          disabled={disabled}
        />
      </div>
    </div>
  );
};
